import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, Loader2, Star, BadgeCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/apiClient";

const SUGGESTIONS = [
  "Recommend a caregiver for dementia",
  "Am I eligible for PASSPORT care?",
  "Help me book care",
];

const initials = (name) => (name || "").replace(/,.*/, "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

function CaregiverCard({ c, onBook }) {
  return (
    <div data-testid={`ai-caregiver-${c.id}`} className="bg-white border border-blue-900/10 rounded-xl p-3 flex gap-3 shadow-sm">
      <span className="grid place-items-center w-12 h-12 rounded-full bg-gradient-to-br from-blue-800 to-blue-600 text-white font-display font-bold shrink-0">{initials(c.name)}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-semibold text-blue-950 truncate">{c.name}</p>
          {c.verified && <BadgeCheck className="w-4 h-4 text-blue-600 shrink-0" />}
        </div>
        <p className="text-[11px] text-slate-500 truncate">{(c.specialties || []).slice(0, 3).join(" · ")}</p>
        <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500">
          <span className="flex items-center gap-0.5 text-amber-600 font-semibold"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {c.rating}</span>
          <span>${c.hourly_rate}/hr</span>
          <span>{c.location}</span>
        </div>
        <button onClick={() => onBook(c.id)} data-testid={`ai-book-${c.id}`}
          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-white bg-blue-900 hover:bg-blue-950 rounded-full px-3 py-1.5 transition-colors">
          Book this caregiver <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

export default function AiAssistant() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: "m0", role: "assistant", content: "Hi! I'm the T5 Care Assistant 💙 Tell me about your situation and I'll recommend the right caregiver, explain PASSPORT eligibility, or book care for you." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(`sess_${Math.random().toString(36).slice(2)}`);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, loading]);

  const book = (id) => { setOpen(false); navigate(`/request-care?caregiver=${id}`); };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { id: `u${Date.now()}`, role: "user", content: msg }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, session_id: sessionId.current, history });
      setMessages((m) => [...m, { id: `a${Date.now()}`, role: "assistant", content: data.reply, caregivers: data.caregivers || [] }]);
    } catch {
      setMessages((m) => [...m, { id: `e${Date.now()}`, role: "assistant", content: "Sorry, I'm having trouble right now. Please call us or submit a care request and our team will help." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button data-testid="ai-toggle-button" onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-[55] grid place-items-center w-16 h-16 rounded-full bg-blue-900 text-white shadow-2xl hover:bg-blue-950 transition-colors">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-7 h-7" />}
      </button>

      {open && (
        <div data-testid="ai-panel" className="fixed bottom-24 right-5 z-[55] w-[calc(100vw-2.5rem)] sm:w-96 h-[560px] max-h-[78vh] bg-white rounded-2xl shadow-2xl border border-blue-900/10 flex flex-col overflow-hidden">
          <div className="bg-blue-900 text-white px-5 py-4 flex items-center gap-2.5">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-white/15"><Sparkles className="w-5 h-5 text-amber-400" /></span>
            <div><p className="font-display font-semibold leading-none">T5 Care Assistant</p><p className="text-[11px] text-blue-200 mt-1">Powered by AI · Serving all 50 states</p></div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAF9F6]">
            {messages.map((m) => (
              <div key={m.id} className="space-y-2">
                <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div data-testid={`ai-msg-${m.role}`} className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-blue-900 text-white rounded-br-sm" : "bg-white text-slate-700 border border-blue-900/5 rounded-bl-sm"}`}>
                    {m.content}
                  </div>
                </div>
                {m.caregivers?.length > 0 && <div className="space-y-2">{m.caregivers.map((c) => <CaregiverCard key={c.id} c={c} onBook={book} />)}</div>}
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-white border border-blue-900/5 rounded-2xl rounded-bl-sm px-3.5 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-blue-700" /></div></div>}
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} data-testid="ai-suggestion" className="text-xs bg-white border border-blue-900/10 text-blue-800 px-3 py-1.5 rounded-full hover:bg-blue-50 transition-colors">{s}</button>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-blue-900/10 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="ai-input" placeholder="Ask about care, courses…"
              className="flex-1 rounded-full border border-blue-900/15 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <Button type="submit" disabled={loading} data-testid="ai-send-button" size="icon" className="rounded-full bg-blue-900 hover:bg-blue-950 text-white shrink-0"><Send className="w-4 h-4" /></Button>
          </form>
        </div>
      )}
    </>
  );
}
